'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className"];
import * as React from 'react';
import clsx from 'clsx';
import { useStore } from '@base-ui/utils/store';
import { styled } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { eventCalendarPreferenceSelectors, eventCalendarViewSelectors } from '@mui/x-scheduler-headless/event-calendar-selectors';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-headless/use-event-calendar-store-context';
import { ErrorContainer } from "../internals/components/error-container/index.mjs";
import { WeekView } from "../week-view/WeekView.mjs";
import { AgendaView } from "../agenda-view/index.mjs";
import { DayView } from "../day-view/DayView.mjs";
import { MonthView } from "../month-view/index.mjs";
import { HeaderToolbar } from "./header-toolbar/index.mjs";
import { ResourcesLegend } from "./resources-legend/index.mjs";
import { MiniCalendar } from "./mini-calendar/index.mjs";
import { useEventCalendarStyledContext } from "./EventCalendarStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EventCalendarRootStyled = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'Root'
})(({
  theme
}) => ({
  // Layout
  boxSizing: 'border-box',
  '*, *::before, *::after': {
    boxSizing: 'inherit'
  },
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  fontFamily: theme.typography.fontFamily
}));
const EventCalendarSidePanel = styled('aside', {
  name: 'MuiEventCalendar',
  slot: 'SidePanel'
})(({
  theme
}) => ({
  minWidth: 250,
  width: 'fit-content',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  borderRadius: theme.shape.borderRadius,
  maxHeight: '100%',
  overflowY: 'hidden'
}));
const EventCalendarMainPanel = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'MainPanel'
})(({
  theme
}) => ({
  display: 'flex',
  flexGrow: 1,
  gap: theme.spacing(1),
  minHeight: 0,
  '&[data-view="month"]': {
    maxHeight: '100%',
    overflow: 'hidden'
  }
}));
const EventCalendarContent = styled('section', {
  name: 'MuiEventCalendar',
  slot: 'Content'
})(() => ({
  display: 'flex',
  flex: 1,
  overflow: 'auto',
  height: '100%',
  maxHeight: '100%',
  '&[data-side-panel-open="false"]': {
    gridColumn: '1 / -1'
  }
}));

/**
 * Internal component that renders the EventCalendar UI.
 * Used by both EventCalendar and EventCalendarPremium.
 * Expects the store to be provided via context.
 */
export const EventCalendarRoot = /*#__PURE__*/React.forwardRef(function EventCalendarRoot(props, forwardedRef) {
  const {
      children,
      className
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const store = useEventCalendarStoreContext();
  const {
    classes
  } = useEventCalendarStyledContext();
  const view = useStore(store, eventCalendarViewSelectors.view);
  const isSidePanelOpen = useStore(store, eventCalendarPreferenceSelectors.isSidePanelOpen);
  let content;
  switch (view) {
    case 'week':
      content = /*#__PURE__*/_jsx(WeekView, {});
      break;
    case 'day':
      content = /*#__PURE__*/_jsx(DayView, {});
      break;
    case 'month':
      content = /*#__PURE__*/_jsx(MonthView, {});
      break;
    case 'agenda':
      content = /*#__PURE__*/_jsx(AgendaView, {});
      break;
    default:
      content = null;
  }
  const rootRef = React.useRef(null);
  const handleRootRef = useMergedRefs(forwardedRef, rootRef);
  return /*#__PURE__*/_jsxs(EventCalendarRootStyled, _extends({
    className: clsx(classes.root, className)
  }, other, {
    ref: handleRootRef,
    children: [/*#__PURE__*/_jsx(HeaderToolbar, {}), /*#__PURE__*/_jsxs(EventCalendarMainPanel, {
      className: classes.mainPanel,
      "data-view": view,
      children: [/*#__PURE__*/_jsx(Collapse, {
        in: isSidePanelOpen,
        orientation: "horizontal",
        className: classes.sidePanelCollapse,
        children: /*#__PURE__*/_jsxs(EventCalendarSidePanel, {
          className: classes.sidePanel,
          children: [/*#__PURE__*/_jsx(MiniCalendar, {}), /*#__PURE__*/_jsx(Divider, {
            className: classes.sidePanelDivider
          }), /*#__PURE__*/_jsx(ResourcesLegend, {})]
        })
      }), /*#__PURE__*/_jsx(EventCalendarContent, {
        className: classes.content,
        "data-view": view,
        "data-side-panel-open": isSidePanelOpen,
        "aria-label": "Calendar content",
        children: content
      })]
    }), /*#__PURE__*/_jsx(ErrorContainer, {}), children]
  }));
});
if (process.env.NODE_ENV !== "production") EventCalendarRoot.displayName = "EventCalendarRoot";